/**
 * 게임 유틸리티 함수들
 * - 숫자 포맷
 * - 경제 공식
 * - 시간 포맷
 */

/**
 * 숫자 포맷 (K, M, B, T, Qa, Qi, Sx, Sp, Oc, No, Dc...)
 */
const SUFFIXES = ['', 'K', 'M', 'B', 'T', 'Qa', 'Qi', 'Sx', 'Sp', 'Oc', 'No', 'Dc', 'UDc', 'DDc', 'TDc', 'QaDc', 'QiDc'];

export function formatNumber(num: number, decimals = 2): string {
  if (num === 0) return '0';
  if (!isFinite(num)) return 'Infinity';

  const isNegative = num < 0;
  num = Math.abs(num);

  if (num < 1000) {
    return (isNegative ? '-' : '') + num.toFixed(decimals > 0 && num < 10 ? decimals : 0);
  }

  const exp = Math.floor(Math.log10(num));
  const index = Math.floor(exp / 3);

  if (index >= SUFFIXES.length) {
    return (isNegative ? '-' : '') + num.toExponential(decimals);
  }

  const scaled = num / Math.pow(1000, index);
  return (isNegative ? '-' : '') + scaled.toFixed(decimals) + SUFFIXES[index];
}

/**
 * 생산기 비용 계산
 */
export function getGeneratorCost(baseCost: number, owned: number, costMultiplier = 1.15): number {
  return Math.floor(baseCost * Math.pow(costMultiplier, owned));
}

/**
 * 대량 구매 비용 계산 (n개)
 */
export function getBulkCost(baseCost: number, owned: number, n: number, costMultiplier = 1.15): number {
  return Math.floor(
    baseCost * Math.pow(costMultiplier, owned) *
    (Math.pow(costMultiplier, n) - 1) / (costMultiplier - 1)
  );
}

/**
 * 시간 포맷 (초 → "1h 23m 45s")
 */
export function formatTime(seconds: number): string {
  if (seconds < 60) return `${Math.floor(seconds)}s`;

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  }
  return `${secs}s`;
}

/**
 * 오프라인 수익 계산
 */
export function calculateOfflineEarnings(
  cps: number,
  elapsedSeconds: number,
  offlineCap: number,
  offlineRate: number
): { earnings: number; duration: number; capped: boolean } {
  const actualDuration = Math.min(elapsedSeconds, offlineCap);
  const earnings = Math.floor(cps * actualDuration * offlineRate);

  return {
    earnings,
    duration: actualDuration,
    capped: elapsedSeconds > offlineCap
  };
}

/**
 * 퍼센트 포맷
 */
export function formatPercent(value: number, decimals = 0): string {
  return `${value.toFixed(decimals)}%`;
}

/**
 * CPS 계산 (생산기 + 버프 포함)
 */
export function calculateCPS(
  generators: Array<{ baseProduction: number; count: number; milestoneMultiplier: number }>,
  globalMultipliers: number[]
): number {
  let totalCPS = 0;

  for (const gen of generators) {
    totalCPS += gen.baseProduction * gen.count * gen.milestoneMultiplier;
  }

  // 글로벌 배율 적용
  for (const mult of globalMultipliers) {
    totalCPS *= mult;
  }

  return totalCPS;
}
